/* Biochemistry Lab Assistant — AI protocol/stats/unit advisor using Gemini (via Emergent LLM).
   Rendered as a standalone tool in the Physiology & Biochemistry category.
*/
import React, { useState } from 'react';
import { FlaskConical, Send, Loader2, Lightbulb, CircleCheck, AlertTriangle } from 'lucide-react';

const TEAL = '#0F766E';
const TEAL_ACTIVE = '#14B8A6';

const API = (typeof window !== 'undefined' && window.location && window.location.origin) || process.env.REACT_APP_BACKEND_URL || '';

const SAMPLE_QUESTIONS = [
  "My Bradford protein assay standard-curve R² = 0.91. Is that acceptable for publication?",
  "I'm measuring SOD activity on 6 wheat genotypes × 2 stress levels × 3 reps. What statistical analysis should I run?",
  "What's the difference between using fresh-weight vs dry-weight basis for proline quantification under drought?",
  "My chlorophyll a/b ratio is 1.4 in stressed plants — is this reasonable or did I make a calculation error?",
  "For DPPH IC50 interpretation, is lower or higher better? And what units should I publish?",
];

export default function BiochemLabAdvisor() {
  const [question, setQuestion] = useState('');
  const [context, setContext] = useState('');
  const [lang, setLang] = useState('en');
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [error, setError] = useState(null);

  const submit = async (q = question) => {
    if (!q.trim()) return;
    setLoading(true); setError(null);
    try {
      const res = await fetch(`${API}/api/biochem-lab-advisor`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question: q, protocol_context: context || undefined, lang }),
      });
      const data = await res.json();
      if (data.error) {
        setError(data.error);
      } else {
        setHistory(h => [{ q, a: data.advice, lang: data.lang, ts: new Date().toISOString() }, ...h]);
        setQuestion('');
      }
    } catch (e) {
      setError(String(e));
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-4" data-testid="biochem-lab-advisor">
      <div className="rounded-xl p-5 text-white" style={{ background: `linear-gradient(135deg, ${TEAL}, ${TEAL_ACTIVE})` }}>
        <div className="flex items-center gap-3 mb-1">
          <FlaskConical size={24} />
          <div>
            <h3 className="text-lg font-bold">Biochemistry Lab Assistant</h3>
            <p className="text-xs opacity-90">Ask about statistical analysis, unit conversions, expected value ranges, and common calculation errors. Powered by Gemini.</p>
          </div>
        </div>
      </div>

      <div className="rounded-xl p-4 bg-white border border-teal-200">
        <label className="text-[11px] uppercase text-gray-600 mb-1 block">Your question</label>
        <textarea
          rows={3}
          value={question}
          onChange={e => setQuestion(e.target.value)}
          placeholder="e.g. What statistical test should I use for SOD activity across 6 genotypes × 2 stress levels?"
          className="w-full px-3 py-2 rounded-md border border-teal-200 text-sm focus:outline-none focus:border-teal-500"
          data-testid="advisor-question"
        />

        <label className="text-[11px] uppercase text-gray-600 mb-1 mt-3 block">Optional protocol context</label>
        <textarea
          rows={2}
          value={context}
          onChange={e => setContext(e.target.value)}
          placeholder="e.g. Method: Giannopolitis & Ries (1977) NBT method. Sample: fresh leaf tissue, 100 mg FW ground in 5 ml phosphate buffer."
          className="w-full px-3 py-2 rounded-md border border-teal-200 text-sm focus:outline-none focus:border-teal-500"
          data-testid="advisor-context"
        />

        <div className="flex items-center justify-between mt-3 gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-600">Language:</label>
            <button onClick={() => setLang('en')}
              className={`text-xs px-3 py-1 rounded-full font-semibold ${lang === 'en' ? 'bg-teal-600 text-white' : 'bg-teal-50 text-teal-800'}`}
              data-testid="lang-en">EN</button>
            <button onClick={() => setLang('hi')}
              className={`text-xs px-3 py-1 rounded-full font-semibold ${lang === 'hi' ? 'bg-teal-600 text-white' : 'bg-teal-50 text-teal-800'}`}
              data-testid="lang-hi">हिंदी</button>
          </div>
          <button
            onClick={() => submit()}
            disabled={loading || !question.trim()}
            className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold text-white disabled:opacity-50 btn-press"
            style={{ background: TEAL }}
            data-testid="advisor-submit"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            {loading ? 'Thinking…' : 'Ask advisor'}
          </button>
        </div>
      </div>

      <div className="rounded-xl p-4 bg-white border border-teal-100">
        <div className="text-xs uppercase tracking-wider font-bold mb-2 flex items-center gap-1" style={{ color: TEAL }}>
          <Lightbulb size={13} /> Sample questions — click to ask
        </div>
        <div className="flex flex-col gap-2">
          {SAMPLE_QUESTIONS.map((q, i) => (
            <button key={i} onClick={() => submit(q)}
              className="text-left text-xs text-gray-700 hover:bg-teal-50 rounded-md px-3 py-1.5 transition"
              data-testid={`sample-q-${i}`}>
              {q}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="rounded-xl p-3 bg-red-50 border border-red-200 flex items-start gap-2" data-testid="advisor-error">
          <AlertTriangle size={16} className="text-red-600 mt-0.5" />
          <div className="text-xs text-red-800">{error}</div>
        </div>
      )}

      {history.length > 0 && (
        <div className="space-y-3" data-testid="advisor-history">
          <div className="text-xs uppercase tracking-wider font-bold" style={{ color: TEAL }}>Conversation</div>
          {history.map((entry, i) => (
            <div key={i} className="rounded-xl p-4 bg-white border border-teal-200" data-testid={`advisor-answer-${i}`}>
              <div className="flex items-start gap-2 mb-2">
                <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center text-xs font-bold text-gray-700">Q</div>
                <div className="flex-1 text-xs text-gray-800 font-medium">{entry.q}</div>
              </div>
              <div className="flex items-start gap-2">
                <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white" style={{ background: TEAL }}>A</div>
                <div className="flex-1 text-sm text-gray-800 whitespace-pre-wrap">{entry.a}</div>
              </div>
              <div className="text-[10px] text-gray-400 mt-2">{new Date(entry.ts).toLocaleString()} · {entry.lang === 'hi' ? 'हिंदी' : 'EN'}</div>
            </div>
          ))}
        </div>
      )}

      <div className="rounded-xl p-3" style={{ background: '#F0FDFA', border: '1px solid #99F6E4' }}>
        <div className="flex items-start gap-2">
          <CircleCheck size={14} className="mt-0.5" style={{ color: TEAL }} />
          <p className="text-[11px] text-gray-700">
            This assistant answers questions on protocol design, statistical analysis, unit conversions, and expected normal ranges for plant physiology &amp; biochemistry experiments. It references standard methods: Arnon (1949), Bradford (1976), Bates et al. (1973), Heath &amp; Packer (1968), Giannopolitis &amp; Ries (1977), Aebi (1984), Nakano &amp; Asada (1981), Brand-Williams et al. (1995).
          </p>
        </div>
      </div>
    </div>
  );
}
